/**
 * adminApi — typed fetchers + React Query hooks for the 6 Admin bucket
 * endpoints. Shapes mirror `backend/src/services/admin-dashboards.types.ts`.
 *
 * Admin endpoints are not region-scoped; the envelope carries a tiny
 * region directory used by the scope picker to navigate into RD views.
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../../lib/apiFetch';

export interface KpiCell {
  label: string;
  value: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  spark?: number[];
}

export interface AdminRegionRow {
  id: string;
  name: string;
  slug: string;
  /** Parent region, or null for a top-level region (BON-446). */
  parentId: string | null;
  /** 0 for a top-level region, 1 for a subregion, 2 for a sub-subregion. */
  depth: number;
}

export interface AdminEnvelope<T> {
  mode: 'live';
  scope: { regions: AdminRegionRow[] };
  data: T;
}

export interface AdminSalesDto {
  kpis: KpiCell[];
  /** Per-region trailing-6-week rollup, sorted by revenue desc. */
  revenueByRegion: Array<{
    region: AdminRegionRow;
    revenueCents: number;
    /** WoW revenue delta %. Null ⇒ no prior-week baseline. */
    deltaPct: number | null;
    ticketsSold: number;
    ticketCap: number;
  }>;
  /** National this-week revenue (cents). Null ⇒ source unavailable. */
  weeklyRevenueCentsNational: number | null;
  /** National weekly revenue, oldest→newest. Null ⇒ source unavailable. */
  nationalTrend: Array<{ weekStartIso: string; revenueCents: number }> | null;
  /** Per-platform ad-spend share this week. Null ⇒ source unavailable. */
  channelMix: Array<{ platform: string; spendCents: number; sharePct: number }> | null;
}

export interface AdminTeachersRegionRow {
  region: AdminRegionRow;
  director: string | null;
  /** Teacher shifts worked over the trailing 6 months — one per assignment,
   *  so this counts shifts, not people. Headcount is the KPI strip. */
  teacherShifts: number;
  /** Assistant shifts over the trailing 6 months, same basis. */
  assistantShifts: number;
  /** Next 6 weeks: workshops with no teacher assigned. */
  staffingIssues: number;
  /** Next 6 weeks: workshops with no staff assigned at all. */
  openShifts: number;
  /** Next 6 weeks: percent of workshops that have a teacher. */
  coveragePct: number;
}

export interface AdminTeachersDto {
  kpis: KpiCell[];
  regionStaffing: AdminTeachersRegionRow[];
}

export interface AdminSuppliesDto {
  /** Fully mocked per BON-151 — the surface wraps in MockedOverlay. */
  kpis: KpiCell[];
  mocked: true;
}

export interface AdminCorpDto {
  /** Fully mocked — frontend wraps the surface in MockedOverlay (BON-154). */
  kpis: KpiCell[];
  mocked: true;
}

export type AdminBucket = 'overview' | 'sales' | 'teachers' | 'supplies' | 'venues' | 'corp';

async function fetchBucket<T>(bucket: AdminBucket): Promise<AdminEnvelope<T>> {
  const res = await apiFetch(`/api/v1/dashboards/admin/${bucket}`);
  if (!res.ok) {
    throw new Error(`Failed to load ${bucket}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as AdminEnvelope<T>;
}

const STALE_30S = 30_000;

export function useAdminSales() {
  return useQuery({
    queryKey: ['dashboards.admin', 'sales'],
    queryFn: () => fetchBucket<AdminSalesDto>('sales'),
    staleTime: STALE_30S,
  });
}

export function useAdminTeachers() {
  return useQuery({
    queryKey: ['dashboards.admin', 'teachers'],
    queryFn: () => fetchBucket<AdminTeachersDto>('teachers'),
    staleTime: STALE_30S,
  });
}

export function useAdminSupplies() {
  return useQuery({
    queryKey: ['dashboards.admin', 'supplies'],
    queryFn: () => fetchBucket<AdminSuppliesDto>('supplies'),
    staleTime: STALE_30S,
  });
}

export function useAdminCorp() {
  return useQuery({
    queryKey: ['dashboards.admin', 'corp'],
    queryFn: () => fetchBucket<AdminCorpDto>('corp'),
    staleTime: STALE_30S,
  });
}
